// Comment stripping stage for normalization pipeline.
// It removes block comments and full-line // comments.
import { findFirstNonWhitespaceIndex, splitLines } from './lines'

// Remove block and full-line comments in one scanner pass.
export function stripLuisComments(source: string): string {
  let output = ''
  let lineBuffer = ''
  let index = 0
  let quote: "'" | '"' | null = null
  let inBlockComment = false
  let skipLineComment = false
  let lineHasContent = false

  // Scan source once and strip comments while preserving non-comment content.
  while (index < source.length) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Flush current line and reset line-scoped states on newline.
    if (char === '\n') {
      if (!skipLineComment) {
        output += `${lineBuffer}\n`
      } else {
        // Drop full-line comment rows from normalized output.
      }
      lineBuffer = ''
      lineHasContent = false
      skipLineComment = false
      index += 1
      continue
    } else {
      // Continue non-newline scanning.
    }

    // Keep skipping text while inside full-line comment mode.
    if (skipLineComment) {
      index += 1
      continue
    } else {
      // Continue scanner outside line-comment mode.
    }

    // Keep block comment content opaque until scanner sees closing `*/`.
    if (inBlockComment) {
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 2
      } else {
        index += 1
      }
      continue
    } else {
      // Continue scanner outside block comment mode.
    }

    // Keep quoted content opaque for comment token detection.
    if (quote) {
      lineBuffer += char
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-string scan.
      }
      if (!isWhitespace(char)) {
        lineHasContent = true
      } else {
        // Keep content flag unchanged for whitespace chars.
      }
      index += 1
      continue
    } else {
      // Continue scanner outside quote mode.
    }

    // Enter quote mode when scanner sees `'` or `"` tokens.
    if (char === "'" || char === '"') {
      quote = char
      lineBuffer += char
      lineHasContent = true
      index += 1
      continue
    } else {
      // Continue comment token checks for unquoted chars.
    }

    // Enter block-comment mode on `/*` token.
    if (char === '/' && next === '*') {
      inBlockComment = true
      index += 2
      continue
    } else if (char === '/' && next === '/' && !lineHasContent) {
      // Enter line-comment mode only for full-line `//` comments.
      skipLineComment = true
      index += 2
      continue
    } else {
      // Keep current char when no comment token is detected.
    }

    lineBuffer += char
    if (!isWhitespace(char)) {
      lineHasContent = true
    } else {
      // Keep content flag unchanged for whitespace chars.
    }
    index += 1
  }

  // Keep trailing non-comment line content at EOF.
  if (!skipLineComment) {
    output += lineBuffer
  } else {
    // Drop trailing full-line comment at EOF.
  }

  // Return source without block and full-line comments.
  return output
}

// Remove `/* ... */` comments with a linear scanner.
export function stripBlockComments(source: string): string {
  const parts: string[] = []
  let index = 0
  let segmentStart = 0
  let inBlockComment = false

  // Walk source once and skip characters while inside block comments.
  while (index < source.length) {
    const char = source[index]
    const next = source[index + 1] ?? ''

    // Exit block-comment mode after matching `*/`.
    if (inBlockComment) {
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 2
        segmentStart = index
        continue
      } else {
        // Keep skipping content inside block comment.
      }

      index += 1
      continue
    } else {
      // Continue scanning outside block comment mode.
    }

    // Enter block-comment mode on `/*`.
    if (char === '/' && next === '*') {
      if (index > segmentStart) {
        parts.push(source.slice(segmentStart, index))
      }
      inBlockComment = true
      index += 2
      continue
    } else {
      // Keep non-comment character in output.
    }

    index += 1
  }

  // Flush remaining non-comment segment.
  if (index > segmentStart && !inBlockComment) {
    parts.push(source.slice(segmentStart, index))
  }

  // Return source without block comments.
  return parts.join('')
}

// Remove full-line `// ...` comments while preserving other line content.
export function stripLineComments(source: string): string {
  const lines = splitLines(source)
  const normalizedLines: string[] = []

  // Keep line order stable and drop only lines that start with `//`.
  for (const line of lines) {
    const firstNonSpace = findFirstNonWhitespaceIndex(line)
    // Skip comment line when first non-space token is `//`.
    if (firstNonSpace !== -1 && line[firstNonSpace] === '/' && line[firstNonSpace + 1] === '/') {
      continue
    } else {
      // Keep non-comment line content unchanged.
    }

    normalizedLines.push(line)
  }

  // Return source without full-line comments.
  return normalizedLines.join('\n')
}

// Check whether character is scanner whitespace.
function isWhitespace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\r' || char === '\f'
}
